import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, Share2, Pencil, Send, Users, Clock, BarChart3, Eye } from 'lucide-react';
import api, { MarketplaceArcheDetail } from '@/lib/api';
import { usePoll, usePollResults } from '../hooks';
import { PollStatusBadge, PollQuestionView, PollResultsView } from '../components';
import type { PollAnswer } from '../types';

const POLLS_ARCHE_ID = 'arche_polls';
const PUBLISH_ACTION = 'publish_poll';

export function PollPage() {
  const { pollId } = useParams<{ pollId: string }>();
  const navigate = useNavigate();
  const { poll, loading, error, refresh } = usePoll(pollId);
  const {
    results,
    loading: resultsLoading,
    error: resultsError,
    refresh: refreshResults,
  } = usePollResults(
    poll && (poll.isCreator || (poll.hasResponded && poll.resultsVisibility === 'public'))
      ? pollId
      : undefined,
  );

  const [answers, setAnswers] = useState<Record<string, PollAnswer>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [publishCostCredits, setPublishCostCredits] = useState<number | null>(null);

  useEffect(() => {
    api
      .getMarketplaceArche(POLLS_ARCHE_ID)
      .then((arche: MarketplaceArcheDetail) => {
        const price = arche.actionPricing?.[PUBLISH_ACTION];
        if (price) setPublishCostCredits(price.costCredits);
      })
      .catch(() => {
        /* non-critical */
      });
  }, []);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-4 pt-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-48 w-full rounded-lg" />
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    );
  }

  if (error || !poll) {
    return (
      <div className="max-w-3xl mx-auto pt-6">
        <Card className="border-destructive/50">
          <CardContent className="flex items-center gap-3 pt-6">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
            <p className="text-muted-foreground">{error || 'Poll not found'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const canVote = poll.status === 'active' && !poll.hasResponded && !poll.isCreator;
  const showResults = poll.isCreator || (poll.hasResponded && poll.resultsVisibility === 'public');

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);

    const answersList = poll.questions.map((q) => {
      const a = answers[q.questionId];
      if (!a) {
        return { questionId: q.questionId, selectedOptions: [], textAnswer: '' };
      }
      return a;
    });

    try {
      await api.submitPollResponse(pollId!, { answers: answersList });
      refresh();
      refreshResults();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAction = async (action: 'publish' | 'close') => {
    setActionError(null);
    try {
      if (action === 'publish') {
        await api.publishPoll(pollId!);
      } else {
        await api.closePoll(pollId!);
      }
      refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : `Failed to ${action} poll`;
      if (message.includes('Insufficient credits')) {
        setActionError('Insufficient credits to publish this poll. Please add more credits.');
      } else {
        setActionError(message);
      }
    }
  };

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/a/arche_polls/${poll.pollId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="max-w-3xl mx-auto pt-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 mb-2">
              <h1 className="text-2xl font-bold">{poll.title}</h1>
              <PollStatusBadge status={poll.status} />
            </div>
            {poll.description && (
              <p className="text-muted-foreground leading-relaxed">{poll.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-5 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" />
            {poll.responseCount} {poll.responseCount === 1 ? 'response' : 'responses'}
          </span>
          {poll.closesAt && (
            <span className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {new Date(poll.closesAt) > new Date() ? 'Closes' : 'Closed'}{' '}
              {new Date(poll.closesAt).toLocaleString()}
            </span>
          )}
        </div>

        {/* Creator actions */}
        {poll.isCreator && (
          <div className="flex items-center gap-2 mt-4">
            {poll.status === 'draft' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/a/arche_polls/${poll.pollId}/edit`)}
                >
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />
                  Edit
                </Button>
                <Button size="sm" onClick={() => handleAction('publish')}>
                  <Send className="h-3.5 w-3.5 mr-1.5" />
                  {publishCostCredits ? `Publish (${publishCostCredits} credits)` : 'Publish'}
                </Button>
              </>
            )}
            {poll.status === 'active' && (
              <>
                <Button variant="outline" size="sm" onClick={handleCopyLink}>
                  <Share2 className="h-3.5 w-3.5 mr-1.5" />
                  {copied ? 'Copied!' : 'Share Link'}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => handleAction('close')}>
                  Close Poll
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {actionError && (
        <div className="mb-6 p-3 bg-destructive/10 text-destructive rounded-lg text-sm flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {actionError}
        </div>
      )}

      {/* Voting form */}
      {canVote && (
        <div className="space-y-3 mb-8">
          {poll.questions.map((q) => (
            <PollQuestionView
              key={q.questionId}
              question={q}
              answer={answers[q.questionId] || { questionId: q.questionId }}
              onChange={(a) => setAnswers((prev) => ({ ...prev, [q.questionId]: a }))}
            />
          ))}

          {submitError && (
            <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {submitError}
            </div>
          )}

          <div className="pt-2">
            <Button onClick={handleSubmit} disabled={submitting}>
              <Send className="h-4 w-4 mr-2" />
              {submitting ? 'Submitting...' : 'Submit Response'}
            </Button>
          </div>
        </div>
      )}

      {/* Already responded message */}
      {poll.hasResponded && !poll.isCreator && poll.resultsVisibility === 'creator_only' && (
        <Card className="mb-6">
          <CardContent className="flex items-center gap-3 pt-6">
            <Eye className="h-5 w-5 text-muted-foreground shrink-0" />
            <p className="text-sm text-muted-foreground">
              You have already responded to this poll. Results are only visible to the poll creator.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {showResults && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Results
          </h2>
          {resultsLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-32 w-full rounded-lg" />
              <Skeleton className="h-32 w-full rounded-lg" />
            </div>
          ) : resultsError ? (
            <p className="text-destructive text-sm">{resultsError}</p>
          ) : results ? (
            <PollResultsView results={results} />
          ) : null}
        </div>
      )}

      {/* Draft questions preview */}
      {poll.isCreator && poll.status === 'draft' && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Questions Preview
          </h2>
          <div className="space-y-3">
            {poll.questions.map((q, i) => (
              <Card key={q.questionId}>
                <CardContent className="pt-5 pb-5">
                  <div className="flex items-start gap-3">
                    <span className="flex items-center justify-center h-6 w-6 rounded-full bg-muted text-xs font-medium text-muted-foreground shrink-0">
                      {i + 1}
                    </span>
                    <div>
                      <p className="font-medium">
                        {q.text}
                        {q.required && <span className="text-destructive ml-1">*</span>}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1 capitalize">
                        {q.type.replace('_', ' ')}
                      </p>
                      {q.options && (
                        <div className="mt-2 space-y-1">
                          {q.options.map((opt, j) => (
                            <div
                              key={j}
                              className="flex items-center gap-2 text-sm text-muted-foreground"
                            >
                              <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/30" />
                              {opt}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
